// Key Rivers & Mountains layer (overview-only, paired with the Map basemap).
// The rivers come straight from the self-hosted tileset (waterway
// centerlines filtered by name); mountain ranges have no polygons, so
// icon spreads and label anchors are hand-placed here. Descriptions live
// in the Supabase geo_features table (sql/03_geo_features.sql).
import { BASEMAP_LABELS_STYLE, BASEMAP_PROBE_URL } from './basemapStyles.js';
import riverLines from './riverLines.json';

export const KEY_RIVERS = [
  'Rhein', 'Mosel', 'Saar', 'Ruwer', 'Ahr', 'Nahe',
  'Main', 'Neckar', 'Elbe', 'Saale', 'Unstrut',
];

// One clickable label anchor per river, placed on the river itself. These
// carry the layer at overview zooms: the tileset only contains the smaller
// rivers (Ruwer, Unstrut...) from z9-10, so tile-derived labels alone
// would miss half the list at the layer's home zoom.
export const RIVER_LABELS = [
  { name: 'Rhein', lat: 50.13, lng: 7.72 },
  { name: 'Mosel', lat: 50.02, lng: 7.25 },
  { name: 'Saar', lat: 49.53, lng: 6.53 },
  { name: 'Ruwer', lat: 49.74, lng: 6.74 },
  { name: 'Ahr', lat: 50.52, lng: 6.93 },
  { name: 'Nahe', lat: 49.79, lng: 7.60 },
  { name: 'Main', lat: 49.83, lng: 9.90 },
  { name: 'Neckar', lat: 49.05, lng: 9.13 },
  { name: 'Elbe', lat: 51.18, lng: 13.48 },
  { name: 'Saale', lat: 51.05, lng: 11.78 },
  { name: 'Unstrut', lat: 51.23, lng: 11.55 },
];

// Icon spreads trace each range; the label sits at a readable anchor.
// peaks: the range's highest named summits (top 1-3, German side) — real
// coordinates from the tileset's mountain_peak data. They render as
// fixed icons ON the summits (not draggable) and feed the range panel.
export const MOUNTAIN_RANGES = [
  { name: 'Haardt', label: [49.31, 7.85], icons: [[49.52, 8.08], [49.45, 8.05], [49.38, 8.02], [49.31, 7.99], [49.24, 7.96], [49.17, 7.93], [49.10, 7.90]],
    peaks: [{ name: 'Kalmit', ele: 673, lat: 49.3167, lng: 8.0764 }, { name: 'Hohe Loog', ele: 619, lat: 49.3369, lng: 8.1103 }, { name: 'Weinbiet', ele: 554, lat: 49.3697, lng: 8.1175 }] },
  { name: 'Hunsrück', label: [49.83, 7.25], icons: [[50.02, 7.42], [49.95, 7.25], [49.88, 6.85], [49.82, 7.45], [49.78, 7.62], [49.72, 7.05], [49.62, 7.25]],
    peaks: [{ name: 'Erbeskopf', ele: 816, lat: 49.7317, lng: 7.0762 }, { name: 'Idarkopf', ele: 746, lat: 49.7622, lng: 7.1858 }] },
  { name: 'Eifel', label: [50.30, 6.70], icons: [[50.50, 6.70], [50.42, 6.35], [50.38, 6.95], [50.32, 6.55], [50.25, 6.85], [50.15, 6.45], [50.10, 6.75]],
    peaks: [{ name: 'Hohe Acht', ele: 747, lat: 50.3853, lng: 7.0044 }, { name: 'Ernstberg', ele: 699, lat: 50.2331, lng: 6.8781 }] },
  { name: 'Taunus', label: [50.22, 8.30], icons: [[50.12, 7.78], [50.15, 7.95], [50.18, 8.15], [50.22, 8.35], [50.25, 8.52], [50.28, 8.70]],
    peaks: [{ name: 'Großer Feldberg', ele: 878, lat: 50.2325, lng: 8.4567 }, { name: 'Kleiner Feldberg', ele: 825, lat: 50.2219, lng: 8.4450 }, { name: 'Altkönig', ele: 798, lat: 50.2231, lng: 8.4781 }] },
  { name: 'Odenwald', label: [49.57, 8.95], icons: [[49.75, 8.90], [49.70, 8.75], [49.62, 9.05], [49.55, 8.85], [49.48, 8.75], [49.45, 8.95]],
    peaks: [{ name: 'Katzenbuckel', ele: 626, lat: 49.4672, lng: 9.0431 }, { name: 'Neunkircher Höhe', ele: 605, lat: 49.7289, lng: 8.7231 }] },
  { name: 'Schwarzwald', label: [48.20, 8.25], icons: [[48.75, 8.35], [48.60, 8.25], [48.42, 8.30], [48.25, 8.20], [48.10, 8.10], [47.95, 8.05], [47.80, 7.95]],
    peaks: [{ name: 'Feldberg', ele: 1493, lat: 47.8739, lng: 8.0044 }, { name: 'Herzogenhorn', ele: 1415, lat: 47.8553, lng: 8.0239 }, { name: 'Belchen', ele: 1414, lat: 47.8222, lng: 7.8331 }] },
  { name: 'Kaiserstuhl', label: [48.04, 7.66], icons: [[48.09, 7.66], [48.06, 7.61]],
    peaks: [{ name: 'Totenkopf', ele: 557, lat: 48.0931, lng: 7.6572 }] },
  { name: 'Spessart', label: [50.05, 9.45], icons: [[50.12, 9.45], [50.05, 9.55], [49.98, 9.35], [49.95, 9.50], [49.88, 9.30]],
    peaks: [{ name: 'Geiersberg', ele: 586, lat: 50.0433, lng: 9.3861 }, { name: 'Hermannskoppe', ele: 567, lat: 50.0831, lng: 9.4469 }] },
  { name: 'Erzgebirge', label: [50.60, 13.25], icons: [[50.48, 12.55], [50.55, 12.85], [50.58, 13.05], [50.62, 13.30], [50.68, 13.70], [50.72, 13.50]],
    peaks: [{ name: 'Fichtelberg', ele: 1215, lat: 50.4283, lng: 12.9536 }, { name: 'Auersberg', ele: 1019, lat: 50.4561, lng: 12.6497 }] },
];

// Border-stretch aliases: along the France border the Rhine's segments
// carry French names in OSM — without these the line breaks southwest.
export const RIVER_ALIASES = {
  'Rhin': 'Rhein', 'Le Rhin': 'Rhein', 'Vieux Rhin': 'Rhein', 'Restrhein': 'Rhein', 'Altrhein': 'Rhein',
};
export const KEY_RIVER_MATCH_NAMES = [...KEY_RIVERS, ...Object.keys(RIVER_ALIASES)];

export const RIVER_NAME = ['coalesce', ['get', 'name:de'], ['get', 'name']];

export const riverFilter = (names) => ['in', RIVER_NAME, ['literal', names]];

// GL overlay style rendered only while the layer is active: the highlight
// line plus line-following labels (larger and brighter than any basemap
// label). Below z9 the tileset lacks the smaller rivers entirely, so the
// extracted centerlines (riverLines.json, scripts/extract_river_lines.mjs)
// carry ALL rivers there and the live tile lines take over from z9.
const RIVER_LINE_PAINT = {
  'line-color': '#4d94c4',
  'line-opacity': 0.85,
  'line-width': ['interpolate', ['exponential', 1.4], ['zoom'], 5, 2, 9, 3.5, 13, 6.5],
};

export const GEO_RIVERS_STYLE = {
  version: 8,
  glyphs: BASEMAP_LABELS_STYLE.glyphs,
  sources: {
    omt: { type: 'vector', url: BASEMAP_PROBE_URL },
    'rivers-low': { type: 'geojson', data: riverLines },
  },
  layers: [
    {
      id: 'geo-river-line-low',
      type: 'line',
      source: 'rivers-low',
      maxzoom: 9,
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: RIVER_LINE_PAINT,
    },
    {
      id: 'geo-river-line',
      type: 'line',
      source: 'omt',
      'source-layer': 'waterway',
      minzoom: 9,
      // aliases keep the line unbroken; the LABEL layer below sticks to
      // canonical names so no French names get drawn
      filter: riverFilter(KEY_RIVER_MATCH_NAMES),
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: RIVER_LINE_PAINT,
    },
    {
      id: 'geo-river-label',
      type: 'symbol',
      source: 'omt',
      'source-layer': 'waterway',
      minzoom: 9,
      filter: riverFilter(KEY_RIVERS),
      layout: {
        'symbol-placement': 'line',
        'symbol-spacing': 400,
        'text-field': RIVER_NAME,
        'text-font': ['Noto Sans Italic'],
        'text-size': ['interpolate', ['linear'], ['zoom'], 9, 13, 13, 16],
        'text-letter-spacing': 0.05,
      },
      paint: {
        'text-color': '#2f6f9f',
        'text-halo-color': 'rgba(255,255,255,0.95)',
        'text-halo-width': 1.6,
      },
    },
  ],
};
